import React, { useState, useMemo } from 'react';
import { Search, ArrowUpDown, Check, Clock } from 'lucide-react';
import { CONNECTORS, CONNECTOR_CATEGORIES, getIngestConnectors, getExportConnectors, getCategoryById } from '../connectorRegistry';

/**
 * ConnectorCatalog — searchable branded tile grid for selecting a connector.
 *
 * Props:
 *   mode: 'source' | 'destination' — filters connectors by direction
 *   selectedId: string | null — currently selected connector ID
 *   onSelect: (connectorId: string) => void
 */
export default function ConnectorCatalog({ mode = 'source', selectedId, onSelect }) {
    const [search, setSearch] = useState('');
    const [activeCategory, setActiveCategory] = useState(null);

    // Filter connectors by direction
    const allConnectors = useMemo(() => {
        return mode === 'source' ? getIngestConnectors() : getExportConnectors();
    }, [mode]);

    // Apply search and category filters
    const filteredConnectors = useMemo(() => {
        let results = allConnectors;

        if (activeCategory) {
            results = results.filter(c => c.category === activeCategory);
        }

        if (search.trim()) {
            const q = search.toLowerCase();
            results = results.filter(c =>
                c.name.toLowerCase().includes(q) ||
                c.description.toLowerCase().includes(q) ||
                c.category.toLowerCase().includes(q)
            );
        }

        return results;
    }, [allConnectors, activeCategory, search]);

    // Get categories that have connectors in current mode
    const visibleCategories = useMemo(() => {
        const categoryIds = new Set(allConnectors.map(c => c.category));
        return CONNECTOR_CATEGORIES.filter(cat => categoryIds.has(cat.id));
    }, [allConnectors]);

    return (
        <div className="space-y-3">
            {/* Search */}
            <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#808080]" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search connectors..."
                    className="w-full pl-9 pr-3 py-2 border border-[#3c3c3c] rounded-lg bg-[#1e1e1e] text-white text-sm placeholder-[#555]"
                />
            </div>

            {/* Category pills */}
            <div className="flex flex-wrap gap-1.5">
                <button
                    onClick={() => setActiveCategory(null)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${!activeCategory
                        ? 'bg-[#3c3c3c] text-white'
                        : 'text-[#808080] hover:text-[#cccccc]'
                        }`}
                >
                    All
                </button>
                {visibleCategories.map(cat => (
                    <button
                        key={cat.id}
                        onClick={() => setActiveCategory(activeCategory === cat.id ? null : cat.id)}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${activeCategory === cat.id
                            ? 'bg-[#3c3c3c] text-white'
                            : 'text-[#808080] hover:text-[#cccccc]'
                            }`}
                    >
                        {cat.label}
                    </button>
                ))}
            </div>

            {/* Connector grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 max-h-[380px] overflow-y-auto pr-1">
                {filteredConnectors.map(connector => {
                    const isSelected = selectedId === connector.id;
                    const isAvailable = connector.status === 'available';
                    const category = getCategoryById(connector.category);

                    return (
                        <button
                            key={connector.id}
                            onClick={() => onSelect(connector.id)}
                            disabled={false} // allow selecting coming_soon to show interest
                            className={`relative flex flex-col items-start gap-1 p-3 rounded-lg border text-left transition-all ${isSelected
                                ? 'border-emerald-500 bg-emerald-900/15 ring-1 ring-emerald-500/30'
                                : 'border-[#3c3c3c] bg-[#1e1e1e] hover:border-[#555] hover:bg-[#252526]'
                                }`}
                        >
                            {/* Selection check */}
                            {isSelected && (
                                <div className="absolute top-2 right-2">
                                    <Check size={14} className="text-emerald-400" />
                                </div>
                            )}

                            {/* Logo + Name */}
                            <div className="flex items-center gap-2.5">
                                <img src={connector.logo} alt={connector.name} className="w-7 h-7 object-contain rounded-sm shrink-0" />
                                <span className={`text-sm font-semibold truncate ${isSelected ? 'text-white' : 'text-[#cccccc]'}`}>
                                    {connector.name}
                                </span>
                            </div>

                            {/* Description */}
                            <p className="text-xs text-[#808080] leading-snug line-clamp-2">
                                {connector.description}
                            </p>

                            {/* Status badge */}
                            <div className="flex items-center gap-1.5 mt-auto pt-1">
                                {!isAvailable && (
                                    <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-[#555] bg-[#252526] border border-[#3c3c3c] rounded px-1.5 py-0.5">
                                        <Clock size={8} />
                                        Coming Soon
                                    </span>
                                )}
                                {isAvailable && (
                                    <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-400 bg-emerald-900/20 border border-emerald-800/30 rounded px-1.5 py-0.5">
                                        ● Ready
                                    </span>
                                )}
                                <span className="text-[10px] text-[#555]">{category?.label}</span>
                            </div>
                        </button>
                    );
                })}
            </div>

            {filteredConnectors.length === 0 && (
                <p className="text-center text-sm text-[#808080] py-6">No connectors match your search.</p>
            )}
        </div>
    );
}

/**
 * ConnectorFields — renders dynamic credential fields for a selected connector.
 *
 * Props:
 *   connectorId: string — the connector whose fields to render
 *   values: object — current field values { key: value }
 *   onChange: (key: string, value: string) => void
 */
export function ConnectorFields({ connectorId, values = {}, onChange }) {
    const connector = CONNECTORS.find(c => c.id === connectorId);
    if (!connector || connector.fields.length === 0) {
        if (connector?.authType === 'none' || connector?.authType === 'certificate') {
            return (
                <p className="text-xs text-[#808080] italic py-2">
                    {connector?.authType === 'none'
                        ? 'No credentials needed — an endpoint URL will be auto-generated.'
                        : 'Certificate-based auth — configuration will be set up during onboarding.'}
                </p>
            );
        }
        return null;
    }

    return (
        <div className="space-y-3 p-3 bg-[#1e1e1e] rounded-lg border border-[#3c3c3c]">
            <p className="text-xs font-semibold text-[#808080] uppercase tracking-wider">
                {connector.name} Configuration
            </p>
            {connector.fields.map(field => (
                <div key={field.key}>
                    <label className="block text-xs text-[#a0a0a0] mb-1">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                    {field.type === 'textarea' ? (
                        <textarea
                            value={values[field.key] || ''}
                            onChange={(e) => onChange(field.key, e.target.value)}
                            placeholder={field.placeholder || ''}
                            rows={3}
                            className="w-full p-2 border border-[#3c3c3c] rounded-lg bg-[#252526] text-white text-sm resize-none"
                        />
                    ) : field.type === 'select' ? (
                        <select
                            value={values[field.key] || ''}
                            onChange={(e) => onChange(field.key, e.target.value)}
                            className="w-full p-2 border border-[#3c3c3c] rounded-lg bg-[#252526] text-white text-sm"
                        >
                            <option value="">-- Select --</option>
                            {(field.options || []).map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    ) : (
                        <input
                            type={field.type === 'password' ? 'password' : field.type === 'number' ? 'number' : 'text'}
                            value={values[field.key] || ''}
                            onChange={(e) => onChange(field.key, e.target.value)}
                            placeholder={field.placeholder || ''}
                            className="w-full p-2 border border-[#3c3c3c] rounded-lg bg-[#252526] text-white text-sm"
                        />
                    )}
                </div>
            ))}
        </div>
    );
}
